import {
    IconChevronLeft,
    IconChevronRight,
    IconMinus,
    IconPlus,
} from '@tabler/icons-react';
import { ZOOM_MAX, ZOOM_MIN } from '../constants';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../utils/bnFormat';
import { findSelected, usePdfEditorStore } from '../state/usePdfEditorStore';
import Hint from '../../../components/common/Hint';

/** A 22px square button, the status bar's only kind. */
function BarBtn({ icon: Icon, label, onClick, disabled }) {
    return (
        <Hint label={label} side="top">
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                aria-label={label}
                className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-md text-[#3D3833] transition-colors hover:bg-[#F4F1ED] disabled:opacity-30 dark:text-zinc-300 dark:hover:bg-zinc-800"
            >
                <Icon size={14} stroke={2} />
            </button>
        </Hint>
    );
}

/**
 * The strip along the bottom: where you are, what you have picked, and how
 * close you are looking.
 *
 * It replaces a dark pill that floated over the page. A pill has to be small
 * enough not to cover the document, which is why it could only ever hold the
 * page number and the zoom — and it covered the foot of the page anyway. A
 * strip costs 30px once and is never in the way.
 */
export default function StatusBar({ scale }) {
    const { t } = useTranslation();
    const pages = usePdfEditorStore((s) => s.pages);
    const currentPage = usePdfEditorStore((s) => s.currentPage);
    const goToPage = usePdfEditorStore((s) => s.goToPage);
    const setZoom = usePdfEditorStore((s) => s.setZoom);
    const zoomIn = usePdfEditorStore((s) => s.zoomIn);
    const zoomOut = usePdfEditorStore((s) => s.zoomOut);
    const selectedId = usePdfEditorStore((s) => s.selectedId);
    const objectsByUid = usePdfEditorStore((s) => s.objectsByUid);

    const selection = findSelected({ selectedId, pages, objectsByUid });
    const step = (delta) => goToPage(Math.min(pages.length - 1, Math.max(0, currentPage + delta)));

    // Under a fit mode the viewport sizes the page from its own width and the
    // stored zoom is not what you are looking at, so the bar reads the scale
    // the viewport reports back.
    const zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale || 1));
    const pct = Math.round(zoom * 100);
    const filled = ((zoom - ZOOM_MIN) / (ZOOM_MAX - ZOOM_MIN)) * 100;

    return (
        <footer className="flex h-[30px] flex-none items-center gap-3.5 border-t border-[#ECE8E4] bg-white px-[13px] dark:border-zinc-800 dark:bg-zinc-900">
            <span className="flex flex-none items-center gap-0.5 text-[11.5px] text-[#6B635C] dark:text-zinc-400">
                <BarBtn
                    icon={IconChevronLeft}
                    label={t('pdfEditor.prevPage')}
                    onClick={() => step(-1)}
                    disabled={currentPage <= 0}
                />
                <span className="px-0.5">
                    {t('pdfEditor.page')}{' '}
                    <span className="font-semibold text-[#1C1815] dark:text-zinc-100">{toBnDigits(currentPage + 1)}</span>
                    {' / '}{toBnDigits(pages.length)}
                </span>
                <BarBtn
                    icon={IconChevronRight}
                    label={t('pdfEditor.nextPage')}
                    onClick={() => step(1)}
                    disabled={currentPage >= pages.length - 1}
                />
            </span>

            <div className="flex-1" />

            {selection?.object ? (
                <span className="truncate text-[11.5px] text-[#6B635C] dark:text-zinc-400">
                    {t('pdfEditor.status.selected', { kind: t(`pdfEditor.kinds.${selection.object.type}`) })}
                </span>
            ) : null}

            <span className="h-4 w-px flex-none bg-[#F0ECE8] dark:bg-zinc-800" />

            <BarBtn icon={IconMinus} label={t('pdfEditor.zoomOut')} onClick={zoomOut} disabled={zoom <= ZOOM_MIN} />

            {/* A native range, painted to the mock: a 3px track with the filled
                part in the accent. Dragging it is the point — the two buttons
                step, this one sweeps. */}
            <input
                type="range"
                min={ZOOM_MIN * 100}
                max={ZOOM_MAX * 100}
                step={1}
                value={pct}
                onChange={(e) => setZoom(Number(e.target.value) / 100)}
                aria-label={t('pdfEditor.status.zoomLevel')}
                className="pdf-zoom-range h-[11px] w-24 flex-none cursor-pointer appearance-none bg-transparent"
                style={{ '--filled': `${filled}%` }}
            />

            <BarBtn icon={IconPlus} label={t('pdfEditor.zoomIn')} onClick={zoomIn} disabled={zoom >= ZOOM_MAX} />

            <span className="min-w-[40px] flex-none text-right text-[11.5px] font-semibold tabular-nums text-[#1C1815] dark:text-zinc-100">
                {toBnDigits(pct)}%
            </span>
        </footer>
    );
}
